import React, { FC, FormEvent, useState } from 'react';
import { WHATSAPP_LINK, CONTACT } from '../data/constants';
import { EnquiryFormProps } from '../types';
import { Send, MessageCircle, CheckCircle, User, Phone, Mail, ChevronDown, FileText } from 'lucide-react';

const PRODUCT_OPTIONS = [
  'Barcode Labels', 'Thermal Transfer Ribbons', 'Barcode Printers',
  'Barcode Scanners', 'Jewellery Labels', 'Taffeta Labels',
  'Retail POS / Billing', 'Software', 'AMC / Repair Service',
  'Technical Support', 'Custom Label Printing', 'Other',
];

interface FormData {
  name: string;
  phone: string;
  email: string;
  product: string;
  message: string;
}

const EnquiryForm: FC<EnquiryFormProps> = ({ productName }) => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    email: '',
    product: productName || '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const endpoint = process.env.REACT_APP_FORM_ENDPOINT;

    if (endpoint) {
      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            submittedAt: new Date().toISOString(),
            page: window.location.href,
          }),
        });
        if (!res.ok) throw new Error('Submission failed');
        setSubmitted(true);
      } catch (err) {
        setError('Could not send enquiry. Please call us or WhatsApp directly.');
      } finally {
        setLoading(false);
      }
    } else {
      // No endpoint configured — show success (dev mode)
      setTimeout(() => {
        setSubmitted(true);
        setLoading(false);
      }, 600);
    }
  };

  if (submitted) {
    return (
      <div className="side-enquiry-card" style={{ textAlign: 'center', padding: '40px 28px' }}>
        <div style={{ width: 56, height: 56, background: 'rgba(34,197,94,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <CheckCircle size={28} color="#22c55e" />
        </div>
        <h3 style={{ fontFamily: 'Montserrat', fontWeight: 900, fontSize: 20, color: 'var(--primary)', marginBottom: 8 }}>
          Enquiry Sent!
        </h3>
        <p style={{ color: 'var(--gray)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
          Thank you! We will contact you within a few hours.
        </p>
        <a href={WHATSAPP_LINK} className="wa-enquiry" target="_blank" rel="noreferrer">
          <MessageCircle size={16} /> Chat on WhatsApp
        </a>
      </div>
    );
  }

  return (
    <div className="side-enquiry-card">
      <h3>Quick Enquiry</h3>
      <p className="subh">Get a callback within 2 hours</p>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 14, color: '#dc2626', fontSize: 13 }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <label htmlFor="enq-name"><User size={12} style={{ display: 'inline', marginRight: 5 }} />Your Name *</label>
          <input
            id="enq-name" type="text" name="name" placeholder="Full Name"
            required value={formData.name} onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="enq-phone"><Phone size={12} style={{ display: 'inline', marginRight: 5 }} />Phone Number *</label>
          <input
            id="enq-phone" type="tel" name="phone" placeholder="+91 XXXXX XXXXX"
            required value={formData.phone} onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="enq-email"><Mail size={12} style={{ display: 'inline', marginRight: 5 }} />Email Address</label>
          <input
            id="enq-email" type="email" name="email" placeholder="you@example.com"
            value={formData.email} onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="enq-product"><ChevronDown size={12} style={{ display: 'inline', marginRight: 5 }} />Product / Service</label>
          <select id="enq-product" name="product" value={formData.product} onChange={handleChange}>
            <option value="">Select Product</option>
            {PRODUCT_OPTIONS.map(o => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="enq-message"><FileText size={12} style={{ display: 'inline', marginRight: 5 }} />Message</label>
          <textarea
            id="enq-message" name="message"
            placeholder="Describe your requirement or quantity needed..."
            value={formData.message} onChange={handleChange}
          />
        </div>
        <button type="submit" className="btn-submit" disabled={loading}>
          {loading ? (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <span className="spinner" /> Sending...
            </span>
          ) : (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <Send size={15} /> Send Enquiry
            </span>
          )}
        </button>
      </form>
      <a
        href={`https://wa.me/${CONTACT.whatsapp}?text=Hi%2C%20I%20am%20interested%20in%20${encodeURIComponent(productName || 'your products')}`}
        className="wa-enquiry" target="_blank" rel="noreferrer"
      >
        <MessageCircle size={16} /> WhatsApp Us Directly
      </a>
    </div>
  );
};

export default EnquiryForm;
